import { FormElements } from '@custom-components';
import Downshift from 'downshift';
import { PropTypes } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { flowRight as compose } from 'lodash';
import { createPortal } from 'react-dom';
import { renderSuggestion, MoreTypingSuggestion, LoadingSuggestion, NoResultSuggestion } from './Suggestions';
import { withGraphAutoComplete } from '../../../hocs/withGraphAutoComplete';
import { StyledPaper } from './GraphAutoComplete.styles';

const blurEvents = [Downshift.stateChangeTypes.mouseUp, Downshift.stateChangeTypes.blurInput];

function AutoCompleteDropdown(props) {
  const { styledProps, getMenuItems, getMenuProps, isCustomDropdownPosition, dropdownPositions } = props;

  if (!isCustomDropdownPosition) {
    return (
      <div {...getMenuProps()}>
        <StyledPaper square styledProps={styledProps}>
          {getMenuItems()}
        </StyledPaper>
      </div>
    );
  }

  return createPortal(
    <div {...getMenuProps()} style={{ ...dropdownPositions, position: 'absolute', height: 300 }}>
      <StyledPaper square styledProps={styledProps}>
        {getMenuItems()}
      </StyledPaper>
    </div>,
    document.body
  );
}

AutoCompleteDropdown.propTypes = {
  styledProps: PropTypes.shape({}).isRequired,
  getMenuItems: PropTypes.func.isRequired,
  getMenuProps: PropTypes.func.isRequired,
  isCustomDropdownPosition: PropTypes.bool.isRequired,
  dropdownPositions: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired
  }).isRequired
};

export function AutoComplete(props) {
  const {
    value: valueObject,
    characterLimit,
    error,
    errorText,
    suggestions,
    loading,
    allowArbitraryInput,
    onValueChange,
    name,
    setFieldValue,
    updateGraphVariable,
    showAllSuggestions,
    clearOnBlur,
    onClick,
    styledProps,
    isCustomDropdownPosition,
    ...InputProps
  } = props;
  const { value = '', query = '' } = valueObject ?? {};
  const canBlur = !allowArbitraryInput && value === '';

  const inputRef = useRef(null);

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [dropdownPositions, setDropdownPositions] = useState({
    top: 0,
    left: 0,
    width: 0
  });

  const inputValueChange = (newQuery, clear = false) => {
    const graphVariable = { value: '', query: newQuery };
    setFieldValue(name, graphVariable, clear);
    updateGraphVariable(graphVariable);
  };

  useEffect(() => {
    if (isDropdownOpened && isCustomDropdownPosition && inputRef.current) {
      const { top, left, width } = inputRef.current.getBoundingClientRect();
      setDropdownPositions({
        width,
        top,
        left
      });
    }
  }, [isDropdownOpened]);

  return (
    <Downshift
      selectedItem={[value]}
      onChange={(suggestion) => {
        if (suggestion) {
          const { label, ...other } = suggestion;
          const change = { query: label, ...other };
          if (onValueChange) onValueChange(name, change);
          if (setFieldValue) setFieldValue(name, change);
        }
      }}
      onInputValueChange={(val, { isOpen }) => {
        if (val !== undefined && isOpen) inputValueChange(val);
      }}
      itemToString={(suggestion) => suggestion?.label}
      inputValue={query}
      onStateChange={({ type, isOpen }) => {
        if (canBlur && !isOpen && blurEvents.includes(type)) {
          inputValueChange('', true);
        }
        if (isOpen !== undefined) {
          setIsDropdownOpened(isOpen);
        }
      }}
    >
      {({ getInputProps, getItemProps, getMenuProps, highlightedIndex, inputValue, isOpen, selectedItem }) => {
        const getMenuItems = () => {
          if (loading) return <LoadingSuggestion />;

          const minQueryLength = onValueChange !== undefined ? 2 : 4;
          const requireMoreTyping = (query?.length || 0) < (characterLimit || minQueryLength);

          if (requireMoreTyping) return <MoreTypingSuggestion />;
          if (!suggestions?.length) return <NoResultSuggestion />;
          const partialSuggestions = suggestions.slice(0, 5);
          return (showAllSuggestions ? suggestions : partialSuggestions).map((suggestion, index) =>
            renderSuggestion({
              suggestion,
              index,
              itemProps: getItemProps({ item: suggestion }),
              highlightedIndex,
              selectedItem,
              inputValue
            })
          );
        };

        const downshiftInputProps = getInputProps({
          onBlur: () => {
            if (clearOnBlur || canBlur) inputValueChange('', true);
          }
        });

        return (
          <div style={{ position: 'relative' }}>
            <FormElements.TextInput
              name={name}
              InputProps={{ ref: downshiftInputProps.ref, ...InputProps, autoComplete: 'off' }}
              error={error}
              errorText={errorText}
              variant={InputProps.variant ?? 'outlined'}
              label={InputProps.label}
              required={InputProps.required}
              disabled={InputProps.disabled}
              placeholder={InputProps.placeholder}
              {...downshiftInputProps}
              value={query}
              styledProps={styledProps}
            />

            <div ref={inputRef} />
            {isOpen && (
              <AutoCompleteDropdown
                getMenuProps={getMenuProps}
                styledProps={styledProps}
                getMenuItems={getMenuItems}
                isCustomDropdownPosition={isCustomDropdownPosition}
                dropdownPositions={dropdownPositions}
              />
            )}
          </div>
        );
      }}
    </Downshift>
  );
}

AutoComplete.propTypes = {
  name: PropTypes.string.isRequired, // the name of the formik field
  allowArbitraryInput: PropTypes.bool,
  characterLimit: PropTypes.number,
  loading: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired, // the setFieldValue method from formik
  updateGraphVariable: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    query: PropTypes.string
  }), // the current formik value
  error: PropTypes.bool,
  errorText: PropTypes.string,
  onValueChange: PropTypes.func,
  showAllSuggestions: PropTypes.bool,
  clearOnBlur: PropTypes.bool,
  onClick: PropTypes.func,
  styledProps: PropTypes.shape({}),
  isCustomDropdownPosition: PropTypes.bool
};

AutoComplete.defaultProps = {
  allowArbitraryInput: false,
  characterLimit: 3,
  error: false,
  errorText: '',
  onValueChange: undefined,
  value: { value: '', query: '' },
  showAllSuggestions: false,
  clearOnBlur: false,
  onClick: () => {},
  styledProps: {},
  isCustomDropdownPosition: false
};

const ComposedAutoComplete = compose(withGraphAutoComplete())(AutoComplete);
export default ComposedAutoComplete;
